import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import './loading.css';

function Rows({ count = 4 }) {
  return <div className="space-y-3">{Array.from({ length: count }, (_, i) => (
    <div key={i} className="flex items-center gap-3 rounded-lg border border-border p-4">
      <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1 space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
      <Skeleton className="h-6 w-16" />
    </div>
  ))}</div>;
}

function Cards() {
  return <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map(i => (
    <div key={i} className="space-y-4 rounded-xl border border-border/60 bg-card/80 p-6">
      <div className="flex justify-between"><Skeleton className="h-12 w-12 rounded-xl" /><Skeleton className="h-6 w-20" /></div>
      <Skeleton className="h-5 w-2/3" /><Skeleton className="h-3 w-1/2" /><Skeleton className="h-4 w-3/4" />
    </div>
  ))}</div>;
}

function Form() {
  return <div className="rounded-xl border border-border bg-card p-6 space-y-5">
    <Skeleton className="h-6 w-40" />
    <div className="grid gap-4 md:grid-cols-2">{[1, 2, 3, 4, 5, 6].map(i => (
      <div key={i} className="space-y-2"><Skeleton className="h-3 w-24" /><Skeleton className="h-10 w-full" /></div>
    ))}</div>
    <Skeleton className="h-10 w-32" />
  </div>;
}

function Chart() {
  return <div className="h-full min-h-0 flex items-end gap-3 border-b border-l border-border p-4">
    {[45, 70, 55, 85, 65, 90].map((height, i) => <Skeleton key={i} className="flex-1" style={{ height: `${height}%` }} />)}
  </div>;
}

function Body({ variant }) {
  switch (variant) {
    case 'cards': return <Cards />;
    case 'board': return <>
      <div className="flex flex-wrap gap-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-9 w-48" />)}</div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{[1, 2, 3, 4].map(i => (
        <div key={i} className="rounded-xl border border-border bg-muted/20 p-3 space-y-4"><Skeleton className="h-6 w-28" /><Rows count={3} /></div>
      ))}</div>
    </>;
    case 'editor': return <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="rounded-xl border border-border bg-card p-6 space-y-5"><Skeleton className="h-9 w-3/4" /><Skeleton className="h-10 w-full" /><Skeleton className="h-80 w-full" /></div>
      <Form />
    </div>;
    case 'settings':
    case 'detail': return <><Skeleton className="h-10 w-80 max-w-full" /><Form />{variant === 'detail' && <Rows />}</>;
    case 'chart': return <Chart />;
    case 'calendar': return <div className="rounded-xl border border-border bg-card p-4">
      <div className="grid grid-cols-7 gap-2">{Array.from({ length: 35 }, (_, i) => <Skeleton key={i} className="h-20 sm:h-24" />)}</div>
    </div>;
    case 'dashboard':
    case 'analytics': return <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({ length: variant === 'analytics' ? 8 : 4 }, (_, i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5 space-y-3"><Skeleton className="h-9 w-9 rounded-xl" /><Skeleton className="h-9 w-24" /><Skeleton className="h-3 w-28" /></div>
      ))}</div>
      {variant === 'analytics' ? <div className="grid gap-5 lg:grid-cols-3">{[1, 2].map(i => (
        <div key={i} className={cn('rounded-xl border border-border bg-card p-6 space-y-6', i === 1 && 'lg:col-span-2')}>
          <Skeleton className="h-4 w-40" /><div className="h-[240px]"><Chart /></div>
        </div>
      ))}</div> : <div className="grid gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(18rem,0.8fr)]">
        <div className="rounded-xl border border-border bg-card p-5"><Rows /></div>
        <div className="rounded-xl border border-border bg-card p-5"><Rows /></div>
      </div>}
    </>;
    default: return <Rows />;
  }
}

// Only mount for pending initial requests or code chunks, never to infer empty data.
// The CSS delay reserves space immediately without flashing on fast responses.
export default function ContentSkeleton({ variant = 'list', label = 'Loading content', className, header = false, ...props }) {
  return <div role="status" aria-live="polite" aria-label={label} className={cn('content-skeleton', className)} {...props}>
    <span className="sr-only">{label}</span>
    <div aria-hidden="true" className={variant === 'chart' ? 'h-full' : 'space-y-6'}>
      {header && <div className="space-y-3"><Skeleton className="h-9 w-64 max-w-full" /><Skeleton className="h-4 w-96 max-w-full" /></div>}
      <Body variant={variant} />
    </div>
  </div>;
}
